import { useRef, useState } from "preact/hooks"
import { Input, type InputProps } from "./input"

/** What the field accepts while it is being typed: an optional minus, digits, at most one dot. */
const PARTIAL_DECIMAL = /^-?\d*\.?\d*$/

/**
 * The number a partially typed decimal means so far, or `null` when it means nothing yet
 * ("", "-", ".", "-."). "1." is 1 and "1.2" is 1.2, so a planner typing 1.2 is never bounced.
 */
export function parse_partial_decimal(text: string): number | null {
  const trimmed = text.trim()
  if (!PARTIAL_DECIMAL.test(trimmed)) return null
  const n = parseFloat(trimmed)
  return Number.isFinite(n) ? n : null
}

/** Whether a field showing `text` already reads as `value`, so an outside update need not retype it. */
export function decimal_text_matches(text: string, value: number): boolean {
  const parsed = parse_partial_decimal(text)
  if (parsed == null) return value === 0 && text.trim() === ""
  return Math.abs(parsed - value) < 1e-9
}

export interface DecimalInputProps extends Omit<InputProps, "value" | "onChange" | "onInput" | "type"> {
  value: number
  /** Called with every keystroke that reads as a number; an emptied field reads as 0. */
  onValueChange: (value: number) => void
}

/**
 * A numeric field that keeps what the user typed. A controlled `<input type="number">` whose
 * parent stores a parsed number cannot hold "1." — the browser reports it as empty, the parent
 * writes 0 back, and the dot is gone before the 2 arrives (the cases-per-store field only took
 * decimals through the arrow keys). This one keeps its own text, commits the parsed number on each
 * keystroke, and retypes itself only when the parent's value stops matching what is on screen.
 */
export function DecimalInput({ value, onValueChange, ...rest }: DecimalInputProps) {
  const [text, set_text] = useState<string>(Number.isFinite(value) ? String(value) : "")
  const last_value = useRef(value)

  // Sync from the prop only when it changed outside this field (a catalog price landing, a reset).
  if (value !== last_value.current) {
    last_value.current = value
    if (!decimal_text_matches(text, value)) {
      set_text(Number.isFinite(value) ? String(value) : "")
    }
  }

  function handle_input(e: Event) {
    const raw = (e.currentTarget as HTMLInputElement).value
    if (!PARTIAL_DECIMAL.test(raw)) return
    set_text(raw)
    const parsed = parse_partial_decimal(raw)
    const next = parsed ?? 0
    last_value.current = next
    onValueChange(next)
  }

  return <Input {...rest} type="text" inputMode="decimal" value={text} onInput={handle_input} />
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("parse_partial_decimal", () => {
    it("reads a decimal as it is being typed", () => {
      expect(parse_partial_decimal("1")).toBe(1)
      expect(parse_partial_decimal("1.")).toBe(1)
      expect(parse_partial_decimal("1.2")).toBe(1.2)
      expect(parse_partial_decimal(".5")).toBe(0.5)
      expect(parse_partial_decimal("-0.25")).toBe(-0.25)
    })

    it("means nothing yet when only a sign or a dot is typed, or the text is not a number", () => {
      expect(parse_partial_decimal("")).toBeNull()
      expect(parse_partial_decimal("-")).toBeNull()
      expect(parse_partial_decimal(".")).toBeNull()
      expect(parse_partial_decimal("1.2.3")).toBeNull()
      expect(parse_partial_decimal("abc")).toBeNull()
    })
  })

  describe("decimal_text_matches", () => {
    it("treats '1.' and '1.0' as already showing 1, within float dust", () => {
      expect(decimal_text_matches("1.", 1)).toBe(true)
      expect(decimal_text_matches("1.0", 1)).toBe(true)
      expect(decimal_text_matches("3", 2.9999999999999996)).toBe(true)
      expect(decimal_text_matches("1.2", 1)).toBe(false)
    })

    it("an empty field shows 0 and nothing else", () => {
      expect(decimal_text_matches("", 0)).toBe(true)
      expect(decimal_text_matches("", 5)).toBe(false)
    })
  })
}
